import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { SubtitlesGenerationPrompt } from '@/types/resources';
import { api } from '@/utils/api';

import Form from './Form';

interface Props {
  prompt: SubtitlesGenerationPrompt;
}

const TableRow: FC<Props> = ({ prompt }) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this prompt?`,
        prompt.title,
      );

      if (!confirm) return;

      await api.delete(
        `/v1/distribution/subtitles-generation-prompts/${prompt.id}`,
      );

      await queryClient.invalidateQueries(['subtitles_generation_prompts']);

      alerts.success('Prompt was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>{prompt.title}</Td>
      <Td>{DateTime.fromISO(prompt.createdAt).toFormat('dd/LL/yyyy')}</Td>
      <Td>{prompt.owner ?? '-'}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowEditForm(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditForm && (
            <Form
              promptResource={prompt}
              onClose={() => setShowEditForm(false)}
            />
          )}
        </AnimatePresence>
      </Td>
    </tr>
  );
};

export default TableRow;
